import { useState } from "react";
import { api, formatApiError, downloadBlob } from "@/lib/api";
import { generatePdfClient } from "@/lib/clientPdf";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FileText, Download, Award, Receipt, FileSignature, IdCard,
  MapPin, ClipboardCheck, Trophy, Handshake, User, Briefcase, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";

const DOC_TYPES = [
  { value: "attestation_don", label: "Attestation de don", icon: Award, color: "emerald",
    fields: [
      { name: "donor_name", label: "Nom du donateur", required: true },
      { name: "amount", label: "Montant (FCFA)", type: "number", required: true },
      { name: "purpose", label: "Motif du don" },
      { name: "date", label: "Date du don", type: "date" },
    ] },
  { value: "recu_don", label: "Reçu de don", icon: Receipt, color: "emerald",
    fields: [
      { name: "donor_name", label: "Nom du donateur", required: true },
      { name: "amount", label: "Montant (FCFA)", type: "number", required: true },
      { name: "date", label: "Date", type: "date" },
      { name: "payment_method", label: "Mode de paiement" },
    ] },
  { value: "contrat_travail", label: "Contrat de travail", icon: FileSignature, color: "blue",
    fields: [
      { name: "employee_name", label: "Nom du salarié", required: true },
      { name: "function", label: "Fonction", required: true },
      { name: "salary", label: "Salaire mensuel (FCFA)", type: "number", required: true },
      { name: "start_date", label: "Date de début", type: "date" },
      { name: "duration", label: "Durée (ex: CDI, 12 mois)" },
    ] },
  { value: "attestation_membre", label: "Attestation de membre", icon: IdCard, color: "emerald",
    fields: [
      { name: "member_name", label: "Nom du membre", required: true },
      { name: "member_type", label: "Qualité (Adhérent, Joueuse...)" },
      { name: "since", label: "Membre depuis (date)", type: "date" },
      { name: "photo_url", label: "URL Photo (optionnel)" },
    ] },
  { value: "carte_membre", label: "Carte de membre", icon: IdCard, color: "emerald",
    fields: [
      { name: "member_name", label: "Nom complet", required: true },
      { name: "member_type", label: "Catégorie" },
      { name: "license_number", label: "N° de licence" },
      { name: "photo_url", label: "URL Photo" },
      { name: "valid_until", label: "Valide jusqu'au (jj/mm/aaaa)" },
    ] },
  { value: "ordre_mission", label: "Ordre de mission", icon: MapPin, color: "amber",
    fields: [
      { name: "employee_name", label: "Nom du salarié", required: true },
      { name: "function", label: "Fonction" },
      { name: "destination", label: "Destination", required: true },
      { name: "purpose", label: "Objet de la mission", required: true },
      { name: "start_date", label: "Date début", type: "date" },
      { name: "end_date", label: "Date fin", type: "date" },
    ] },
  { value: "attestation_presence", label: "Attestation de présence", icon: ClipboardCheck, color: "blue",
    fields: [
      { name: "person_name", label: "Nom de la personne", required: true },
      { name: "event", label: "Événement", required: true },
      { name: "event_date", label: "Date de l'événement", type: "date" },
    ] },
  { value: "attestation_sportive", label: "Attestation sportive", icon: Trophy, color: "emerald",
    fields: [
      { name: "player_name", label: "Nom de la joueuse", required: true },
      { name: "category", label: "Catégorie (PRO, Amateur...)" },
      { name: "season", label: "Saison (ex: 2025-2026)" },
    ] },
  { value: "convention_partenariat", label: "Convention de partenariat", icon: Handshake, color: "blue",
    fields: [
      { name: "partner_name", label: "Nom du partenaire", required: true },
      { name: "amount", label: "Montant du partenariat (FCFA)", type: "number" },
      { name: "duration", label: "Durée (ex: 12 mois)" },
    ] },
  { value: "fiche_joueur", label: "Fiche joueur", icon: User, color: "slate",
    fields: [
      { name: "first_name", label: "Prénom", required: true },
      { name: "last_name", label: "Nom", required: true },
      { name: "category", label: "Catégorie" },
      { name: "position", label: "Poste" },
      { name: "date_of_birth", label: "Date de naissance", type: "date" },
      { name: "license_number", label: "N° Licence" },
      { name: "height_cm", label: "Taille (cm)", type: "number" },
      { name: "weight_kg", label: "Poids (kg)", type: "number" },
    ] },
  { value: "fiche_salarie", label: "Fiche salarié", icon: Briefcase, color: "slate",
    fields: [
      { name: "first_name", label: "Prénom", required: true },
      { name: "last_name", label: "Nom", required: true },
      { name: "function", label: "Fonction" },
      { name: "staff_type", label: "Catégorie" },
      { name: "hire_date", label: "Date d'embauche", type: "date" },
      { name: "salary", label: "Salaire (FCFA)", type: "number" },
      { name: "contract_type", label: "Contrat" },
      { name: "phone", label: "Téléphone" },
    ] },
  { value: "fiche_paie", label: "Fiche de paie", icon: ScrollText, color: "emerald",
    fields: [
      { name: "employee_name", label: "Nom du salarié", required: true },
      { name: "function", label: "Fonction" },
      { name: "month", label: "Mois (ex: Janvier 2026)", required: true },
      { name: "base_salary", label: "Salaire de base (FCFA)", type: "number", required: true },
      { name: "bonus", label: "Primes (FCFA)", type: "number" },
      { name: "deductions", label: "Déductions (FCFA)", type: "number" },
    ] },
];

export default function Documents() {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});

  const startGenerate = (docType) => {
    setSelectedType(docType);
    setFormData({});
    setOpen(true);
  };

  const generate = async () => {
    // If offline, go straight to client-side generation
    if (!navigator.onLine) {
      try {
        await generatePdfClient(selectedType.value, formData);
        toast.success("PDF généré hors-ligne avec succès");
        setOpen(false);
      } catch (e) {
        toast.error("Erreur de génération hors-ligne : " + (e.message || ""));
      }
      return;
    }
    try {
      const res = await api.post("/documents/generate", {
        document_type: selectedType.value,
        data: formData,
      }, { responseType: "blob" });
      // If response is JSON (offline-queued synthetic 202), fallback to client
      const ct = res.headers?.["content-type"] || "";
      if (res.status === 202 || !ct.includes("pdf")) {
        await generatePdfClient(selectedType.value, formData);
        toast.success("PDF généré hors-ligne");
      } else {
        downloadBlob(res.data, `${selectedType.value}.pdf`, "application/pdf");
        toast.success("Document généré avec succès");
      }
      setOpen(false);
    } catch (e) {
      // Network failure → client fallback
      if (!e.response) {
        try {
          await generatePdfClient(selectedType.value, formData);
          toast.success("PDF généré hors-ligne (serveur injoignable)");
          setOpen(false);
          return;
        } catch (ce) {
          toast.error("Erreur de génération : " + (ce.message || ""));
          return;
        }
      }
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed.detail || txt;
        } catch {
          detail = "Erreur de génération du PDF";
        }
      }
      toast.error(formatApiError(detail) || "Erreur de génération");
    }
  };

  return (
    <div data-testid="documents-page">
      <PageHeader title="Génération de documents" subtitle="12 modèles officiels personnalisables — logo, cachet et signatures du club" />

      <div className="p-6 lg:p-8 space-y-8">
        {/* Grid of document types */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
            Modèles disponibles
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {DOC_TYPES.map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.value}
                  onClick={() => startGenerate(d)}
                  data-testid={`doc-card-${d.value}`}
                  className="stat-card bg-white border border-slate-200 rounded-md p-5 text-left group"
                >
                  <div className="h-10 w-10 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3 group-hover:bg-emerald-600 group-hover:border-emerald-600 transition-colors">
                    <Icon className="h-5 w-5 text-emerald-700 group-hover:text-white" />
                  </div>
                  <div className="font-barlow font-bold text-base tracking-tight text-slate-900 leading-tight">
                    {d.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Download className="h-3 w-3" /> Générer PDF
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {selectedType?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {selectedType?.fields.map((f) => (
              <div key={f.name} className={f.label.length > 25 ? "col-span-2" : ""}>
                <Field
                  label={f.label}
                  type={f.type || "text"}
                  value={formData[f.name] || ""}
                  onChange={(v) => setFormData({ ...formData, [f.name]: v })}
                  required={f.required}
                  testid={`docfield-${f.name}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={generate} data-testid="generate-doc-button"
              className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="h-4 w-4 mr-2" /> Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
